import { compareDesc } from "date-fns";
import { deburr } from "lodash-es";

import { ComputedRole, getCanViewPage } from "../../shared/capabilities";

import { NestedAssessmentType } from "./types";

import {
  ActivityCategory,
  ActivityPageType,
  ActivityType,
  PageCategory,
  PageType,
  UserInformationType,
} from "components/server-types";
import { DEFAULT_ORDER_FN, ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { ActivityWithPagesType } from "store/selectors";
import type { UserWithSectionActivity } from "store/selectors/types";

const ORDERED_ACTIVITY_CATEGORIES = Object.values(ActivityCategory);
export const sortActivities = (a: ActivityType, b: ActivityType) => {
  const prioA = ORDERED_ACTIVITY_CATEGORIES.indexOf(a.category);
  const prioB = ORDERED_ACTIVITY_CATEGORIES.indexOf(b.category);

  if (prioA === prioB) {
    return a.order - b.order;
  }

  return prioA - prioB;
};

export const getSortedPages = (activities: ActivityWithPagesType[]) => {
  const pages: ActivityPageType[] = [];
  for (const activity of activities) {
    pages.push(...activity.pages.sort(DEFAULT_ORDER_FN));
  }
  return pages;
};

export const sortPagesForTopic = (
  topicId: string,
  activities: ActivityType[],
  pages: PageType[],
) => {
  const sortedActivities = activities
    .filter((activity) => activity.topic_id === topicId)
    .sort(sortActivities);

  return getSortedPages(
    sortedActivities.map((activity) => ({
      ...activity,
      pages: pages.filter((page) => page.activity_id === activity.id),
    })) as ActivityWithPagesType[],
  );
};

export const sortTopicActivities = (
  topicId: string,
  activities: ActivityType[],
  pages: PageType[],
  computedRole: ComputedRole,
) => {
  const sortedActivities = activities
    .filter((activity) => activity.topic_id === topicId)
    .sort(sortActivities);

  return sortedActivities.map((activity) => {
    const activityPages = pages.filter(
      (page) => page.activity_id === activity.id && getCanViewPage(page, computedRole),
    );
    return { ...activity, pages: activityPages } as ActivityWithPagesType;
  });
};

export const sortByTermYearRegex = (a: { title: string }, b: { title: string }) => {
  // Extract valid years: 4-digit years between 2000-2050, or 2-digit years between 00-50
  const getValidYear = (title: string): { year: number; is2Digit: boolean } | null => {
    // Try 4-digit year first
    const fourDigitMatch = title.match(/(\d{4})/);
    if (fourDigitMatch) {
      const year = parseInt(fourDigitMatch[1], 10);
      if (year >= 2000 && year <= 2050) {
        return { year, is2Digit: false };
      }
    }

    // Try 2-digit year (treat as 20XX)
    const twoDigitMatch = title.match(/\b(\d{2})\b/);
    if (twoDigitMatch) {
      const year = parseInt(twoDigitMatch[1], 10);
      if (year >= 0 && year <= 50) {
        return { year: 2000 + year, is2Digit: true };
      }
    }

    return null;
  };

  const aYearInfo = getValidYear(a.title);
  const bYearInfo = getValidYear(b.title);

  // If no year, sort by title alphabetically
  if (!aYearInfo && !bYearInfo) {
    // eslint-disable-next-line babel/new-cap
    return ORDER_BY_TITLE(a, b);
  }

  // Overall, terms with years should appear before terms without
  if (aYearInfo && !bYearInfo) {
    return -1;
  }
  if (!aYearInfo && bYearInfo) {
    return 1;
  }

  // Both have years - sort by year descending first
  if (aYearInfo!.year !== bYearInfo!.year) {
    return bYearInfo!.year - aYearInfo!.year; // Descending order
  }

  // Same year - custom sorting that prioritizes shorter year formats
  // Extract the non-year part of the title for comparison
  const getNonYearPart = (title: string, yearInfo: { year: number; is2Digit: boolean }) => {
    if (yearInfo.is2Digit) {
      // Remove 2-digit year
      return title.replace(/\b\d{2}\b/, "").trim();
    }
    // Remove 4-digit year
    return title.replace(/\d{4}/, "").trim();
  };

  const aNonYear = getNonYearPart(a.title, aYearInfo!);
  const bNonYear = getNonYearPart(b.title, bYearInfo!);

  // If the non-year parts are the same, prioritize 2-digit format
  if (aNonYear === bNonYear) {
    if (aYearInfo!.is2Digit !== bYearInfo!.is2Digit) {
      return aYearInfo!.is2Digit ? -1 : 1; // 2-digit comes first
    }
  }

  // Otherwise sort alphabetically
  // eslint-disable-next-line babel/new-cap
  return ORDER_BY_TITLE(a, b);
};

const transformForSort = (name: string) => deburr(name).toLowerCase();

export const sortByFirstName = (a: UserInformationType, b: UserInformationType) => {
  const aKey =
    `${transformForSort(a.first_name ?? "")} ${transformForSort(a.last_name ?? "")}`.trim();
  const bKey =
    `${transformForSort(b.first_name ?? "")} ${transformForSort(b.last_name ?? "")}`.trim();
  return aKey.localeCompare(bKey, undefined, { sensitivity: "base", numeric: true });
};

export const getActiveStateSortKeyForUser = (
  user: Omit<UserWithSectionActivity, "section_user_id">,
) => {
  return ["active", "inactive", "offline"].indexOf(user.sectionActivityStatus) > 1 ? 1 : 0;
};

export const sortByActiveStateThenFirstName = (
  a: Omit<UserWithSectionActivity, "section_user_id">,
  b: Omit<UserWithSectionActivity, "section_user_id">,
) => {
  const activeStateDiff = getActiveStateSortKeyForUser(a) - getActiveStateSortKeyForUser(b);
  if (activeStateDiff !== 0) return activeStateDiff;
  return sortByFirstName(a, b);
};

/**
 * Consolidated sorting logic for pages with assessments.
 *
 * This function implements a two-tier sorting strategy:
 * 1. Pages with assessments are sorted by assessment date (most recent first)
 * 2. Pages without assessments are sorted by category and order
 *
 * Pages with assessments always appear before pages without assessments.
 */
export const sortPagesWithAssessments = <
  T extends {
    assessment?: NestedAssessmentType;
    category: PageCategory;
    order: number;
  },
>(
  pages: T[],
): T[] => {
  const pagesWithAssessments = pages.filter((page) => page.assessment);
  const pagesWithoutAssessments = pages.filter((page) => !page.assessment);
  const sortedPagesWithAssessments = pagesWithAssessments.sort(comparePagesByDateAssessed);
  const sortedPagesWithoutAssessments = pagesWithoutAssessments.sort(
    comparePagesByCategoryAndOrder,
  );
  return [...sortedPagesWithAssessments, ...sortedPagesWithoutAssessments];
};

/**
 * Compares pages by their category and order.
 * Category priority: assignment > activity_page > course_resource
 * Within the same category, pages are sorted by their order value.
 */
export const comparePagesByCategoryAndOrder = <T extends { category: PageCategory; order: number }>(
  pageA: T,
  pageB: T,
) => {
  if (pageA.category !== pageB.category) {
    const pageTypeOrder: PageCategory[] = ["assignment", "activity_page", "course_resource"];
    return pageTypeOrder.indexOf(pageA.category) - pageTypeOrder.indexOf(pageB.category);
  }
  // TODO: Preferably we'd also order these by their parent topic order
  return pageA.order - pageB.order;
};

/**
 * Compares pages by their assessment date.
 */
export const comparePagesByDateAssessed = <T extends { assessment?: NestedAssessmentType }>(
  pageA: T,
  pageB: T,
) => {
  const assessmentA = pageA.assessment;
  const assessmentB = pageB.assessment;

  // If neither page has an assessment, maintain original order
  if (!assessmentA && !assessmentB) return 0;

  // If only one page has an assessment, put the one with assessment first
  if (!assessmentA) return 1;
  if (!assessmentB) return -1;

  // Compare by assessment creation date (most recent first)
  return compareDesc(assessmentA.created_at, assessmentB.created_at);
};
